import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  IconMail,
  IconBrandFacebook,
  IconBrandInstagram,
  IconBrandYoutube,
  IconBrandWhatsapp,
  IconUserPlus,
  IconInfoCircle,
} from '@tabler/icons';

import api from '../../services/api';
import { useAuth } from '../../hooks/auth';
import { useToast } from '../../hooks/toast';
import { useOnlineStatus } from '../../hooks/onlineStatus';

import BackHeader from '../../components/BackHeader';
import Button from '../../components/Button';
import Modal from '../../components/Modal';
import Offline from '../../components/OfflineMessage';
import PropertyItem from '../../components/PropertyItem';
import { H2, Skeleton, NotFoundContainer } from '../../components/Generic';
import { Container, Content, AvatarContainer, Name, Bio, Card } from './styles';

import userImg from '../../assets/images/user-avatar.png';

const Profile: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [profile, setProfile] = useState<any>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [properties, setProperties] = useState<any[]>([]);

  const { id }: any = useParams();
  const { user } = useAuth();
  const { addToast } = useToast();
  const history = useHistory();
  const isOnline = useOnlineStatus();

  useEffect(() => {
    if (id === user.id) history.replace('/perfil');

    getData();
  }, []);

  const getData = async () => {
    try {
      const profileResponse = await api.get(`/usuarios/perfil/${id}`);
      setProfile(profileResponse.data);

      const propertiesResponse = await api.get('/propriedades/minhas');
      setProperties(propertiesResponse.data.minhasPropriedades);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro!',
        description: err.response.data.message || 'Algo deu errado!',
      });
    } finally {
      setLoading(false);
    }
  };

  const setContributor = useCallback(
    async (id_property: string, profile_id: string) => {
      try {
        setActionLoading(id_property);

        await api.post(
          `/colaboradores/`,
          {
            usuario_id: profile_id,
          },
          {
            headers: {
              propriedade_id: id_property,
            },
          },
        );

        setIsModalOpen(false);

        addToast({
          type: 'success',
          title: 'Sucesso!',
          description: 'Usuário adicionado como colaborador.',
        });

        history.push(`/propriedade/${id_property}`);
      } catch (err) {
        addToast({
          type: 'error',
          title: 'Erro!',
          description: err.response.data.message || 'Algo deu errado!',
        });
      } finally {
        setActionLoading('');
      }
    },
    [useToast],
  );

  return (
    <Container>
      <BackHeader title="Usuário" />

      {!isOnline ? (
        <Offline />
      ) : loading ? (
        <Content>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Skeleton style={{ height: 150, width: 150, borderRadius: 75 }} />
            <Skeleton style={{ height: 40, width: 260, marginTop: 30 }} />
            <Skeleton style={{ height: 20, width: 160, marginTop: 20 }} />
            <Skeleton style={{ height: 160, width: '100%', marginTop: 40 }} />
          </div>
        </Content>
      ) : (
        <Content>
          <AvatarContainer>
            <img src={profile?.url || userImg} alt="Foto" />
          </AvatarContainer>

          <Name>{profile?.nome}</Name>
          <Bio>{profile?.formacao_profissional}</Bio>

          <Button
            onClick={() => setIsModalOpen(true)}
            style={{ marginBottom: 40 }}
          >
            <IconUserPlus size={24} />
            Adicionar como colaborador
          </Button>

          <Card>
            <label>Social</label>

            {profile?.whatsapp_numero && (
              <a
                href={`https://api.whatsapp.com/send?phone=+55${profile?.whatsapp_numero.replaceAll(
                  ' ',
                  '',
                )}`}
              >
                <IconBrandWhatsapp size={24} /> WhatsApp
              </a>
            )}

            {profile?.facebook_nome && (
              <a
                href={`https://www.facebook.com/${profile?.facebook_nome.replace(
                  '@',
                  '',
                )}`}
              >
                <IconBrandFacebook size={24} /> Facebook
              </a>
            )}

            {profile?.instagram_nome && (
              <a
                href={`https://www.instagram.com/${profile?.instagram_nome.replace(
                  '@',
                  '',
                )}`}
              >
                <IconBrandInstagram size={24} /> Instagram
              </a>
            )}

            {profile?.youtube_link && (
              <a href={`${profile?.youtube_link}`}>
                <IconBrandYoutube size={24} /> YouTube
              </a>
            )}
          </Card>

          <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
            <H2>Escolha uma propriedade</H2>

            {properties?.length > 0 ? (
              properties?.map(property => (
                <PropertyItem
                  key={property.id}
                  location={property.descricao}
                  name={property.nome}
                  imageUrl={
                    property.imagens?.length > 0
                      ? property.imagens[0]?.image_url
                      : ''
                  }
                  onClick={() => setContributor(property.id, profile.id)}
                  isLoading={actionLoading === property.id}
                />
              ))
            ) : (
              <NotFoundContainer>
                <IconInfoCircle size={24} />

                <strong>Nenhuma propriedade encontrada.</strong>
              </NotFoundContainer>
            )}
          </Modal>
        </Content>
      )}
    </Container>
  );
};

export default Profile;
